import React from "react";
import JSONInspector from "@/modules/racket_lab_admin/JSONInspector";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * DryRunDiffPanel — Sub-sprint D-α.
 *
 * Renders the dry-run response from a `useDryRunApply` hook. Shows
 * current_state vs proposed_state side-by-side, masks the
 * confirmation_token (only last 8 chars are surfaced), and renders a
 * red kill-switch banner if `kill_switch_active=true`.
 */
const _mask = (tok) => {
  if (!tok || typeof tok !== "string") return "";
  if (tok.length <= 8) return "•".repeat(tok.length);
  return `${"•".repeat(Math.max(0, tok.length - 8))}${tok.slice(-8)}`;
};

const Side = ({ title, value, testId }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{title}</span>
    <div className="min-h-[80px] rounded-md border border-slate-200 bg-slate-50 p-2">
      <JSONInspector value={value ?? null} dataTestId={testId} />
    </div>
  </div>
);

export const DryRunDiffPanel = ({ dryRun, stale = false, dataTestId = "rl-dry-run-diff" }) => {
  const t = useRacketLabAdminT();
  if (!dryRun) {
    return (
      <div data-testid={`${dataTestId}-empty`} className="rounded-md border border-dashed border-slate-300 bg-white p-4 text-xs text-slate-500">
        {t("ops.dry_run.empty")}
      </div>
    );
  }
  const killed = dryRun.kill_switch_active === true;
  return (
    <div data-testid={dataTestId} className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid={`${dataTestId}-mode`} className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-800">
          mode: {dryRun.mode || "dry_run"}
        </span>
        {dryRun.would_create !== undefined && (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
            would_create: {String(dryRun.would_create)}
          </span>
        )}
        {dryRun.would_update !== undefined && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
            would_update: {String(dryRun.would_update)}
          </span>
        )}
        {stale && (
          <span data-testid={`${dataTestId}-stale`} className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-800">
            {t("ops.dry_run.stale_pill")}
          </span>
        )}
      </div>

      {killed && (
        <div data-testid={`${dataTestId}-killswitch`} className="rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900">
          {t("ops.dry_run.killswitch_banner")}
        </div>
      )}

      <div className="flex flex-col gap-3 md:flex-row">
        <Side title="current_state" value={dryRun.current_state ?? null} testId={`${dataTestId}-current`} />
        <Side title="proposed_state" value={dryRun.proposed_state ?? null} testId={`${dataTestId}-proposed`} />
      </div>

      <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-[11px]">
        <span className="text-slate-600">{t("ops.dry_run.token_label")}</span>
        <code data-testid={`${dataTestId}-token-mask`} className="font-mono text-slate-800">{_mask(dryRun.confirmation_token)}</code>
      </div>
      {dryRun.next_step && (
        <p className="text-[11px] text-slate-500">{dryRun.next_step}</p>
      )}
    </div>
  );
};

export default DryRunDiffPanel;
